import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import { useMutation } from "@apollo/react-hooks";
import { Box, Button, TextField } from "@mui/material";
import { MY_HR_CREATE_NEW_PASSWORD } from "my-hr/graphql";
import { useMyHrEmployee } from "my-hr/hooks";
import { OcLoading, OcPasswordInputField } from "oc/components";
import {
  useOcCurrentUserState,
  useOcPasswordInput,
  useOcProperty,
  useOcSnackbar,
} from "oc/hooks";
import { ocIsTemporaryPasswordState } from "oc/states";

type MyHrChangePasswordFormProps = {
  onLogout: () => void;
};

export const MyHrChangePasswordForm = ({
  onLogout,
}: MyHrChangePasswordFormProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const { data: passwordPolicy } = useOcProperty({
    resource: "system",
    key: "passwordPolicy",
  });

  const { setOpenMessage } = useOcSnackbar();
  const [loading, setLoading] = useState(false);

  const { employeeCode, emailAddress } = useOcCurrentUserState();
  const { data: employeeData } = useMyHrEmployee({ employeeId: employeeCode });
  const taxNumber = employeeData?.taxNumber.trim() || "";

  const [correctTaxNumber, setCorrectTaxNumber] = useState("");
  const [taxNumberError, setTaxNumberError] = useState(false);

  const {
    value: password,
    setValue: setPassword,
    isValid: isPasswordValid,
    validate: validatePassword,
    error: passwordError,
    setError: setPasswordError,
    helperText: passwordHelperText,
    setHelperText: setPasswordHelperText,
  } = useOcPasswordInput();

  const {
    value: passwordAgain,
    setValue: setPasswordAgain,
    isValid: isPasswordAgainValid,
    validate: validatePasswordAgain,
    error: passwordAgainError,
    setError: setPasswordAgainError,
    helperText: passwordAgainHelperText,
    setHelperText: setPasswordAgainHelperText,
  } = useOcPasswordInput();

  const [temporaryPasswordState, setIsTemporaryPassword] = useRecoilState(
    ocIsTemporaryPasswordState
  );


  const [createNewPassword] = useMutation(MY_HR_CREATE_NEW_PASSWORD, {
    onCompleted: (data) => {
      if (data?.createNewPassword?.status === "ok") {
        setIsTemporaryPassword(false);
        setOpenMessage({
          type: "success",
          message: t("MY_HR_PASSWORD_CHANGE_SUCCEEDED"),
        });
      }
      setLoading(false);
    },
    onError: (error) => {
      console.log(error);
      setLoading(false);
      setPasswordError(true);
      setPasswordHelperText(t("MY_HR_ERROR_MESSAGE_PASSWORD_IS_WRONG"),);
    },
  });
  useEffect(() => {
    if (temporaryPasswordState === false) {
      history.push("/");
    }
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isPasswordValid({ checkPasswordPolicy: true })) {
      validatePassword({ checkPasswordPolicy: true });
      return;
    }

    if (!isPasswordAgainValid({ checkPasswordPolicy: true })) {
      validatePasswordAgain({ checkPasswordPolicy: true });
    }

    if (correctTaxNumber !== taxNumber) {
      setTaxNumberError(true);
      return;
    }

    if (password !== passwordAgain) {
      setPasswordError(true);
      setPasswordHelperText(
        t("MY_HR_ERROR_MESSAGE_TWO_PASSWORD_IS_DIFFERENT")
      );
      setPasswordAgainError(true);
      setPasswordAgainHelperText(
        t("MY_HR_ERROR_MESSAGE_TWO_PASSWORD_IS_DIFFERENT")
      );
      return;
    }
    if (
      !isPasswordValid({ checkPasswordPolicy: true }) ||
      !isPasswordAgainValid({ checkPasswordPolicy: true })
    ) {
      return;
    }
    setLoading(true);
    createNewPassword({
      variables: {
        email: emailAddress,
        password,
      },
    });
  };

  const handleLogout = () => {
    onLogout();
  };

  return (
    <>
      <OcLoading show={loading} />
      <form noValidate autoComplete="off" onSubmit={handleSubmit}>
        <Box
          paddingTop="32px"
          fontSize="12px"
          letterSpacing="0.4px"
          color="#4d4d4d"
        >
          {passwordPolicy === "strong"
            ? t("MY_HR_INFO_TEXT_STRONG_PASSWORD")
            : t("MY_HR_INFO_TEXT_NOT_STRONG_PASSWORD")}
        </Box>
        <Box paddingTop="36px" paddingBottom="14px">
          <OcPasswordInputField
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={passwordError}
            setError={setPasswordError}
            helperText={passwordHelperText}
            setHelperText={setPasswordHelperText}
            label={t("MY_HR_NEW_PASSWORD")}
          />
        </Box>
        <Box paddingTop="18px" paddingBottom="36px">
          <OcPasswordInputField
            value={passwordAgain}
            onChange={(e) => setPasswordAgain(e.target.value)}
            error={passwordAgainError}
            setError={setPasswordAgainError}
            helperText={passwordAgainHelperText}
            setHelperText={setPasswordAgainHelperText}
            label={t("MY_HR_CONFIRM_NEW_PASSWORD")}
          />
        </Box>
        <TextField
          id="correctTaxNumber"
          value={correctTaxNumber}
          variant="outlined"
          fullWidth
          label={t("MY_HR_TAX_NUMBER")}
          type="string"
          error={taxNumberError}
          helperText={
            taxNumberError &&
            t("MY_HR_CHANGE_TEMPORARY_PASSWORD_TAX_NUMBER_ERROR")
          }
          onFocus={() => setTaxNumberError(false)}
          onChange={(e) => setCorrectTaxNumber(e.target.value)}
        />
        <Box height="36px" />
        <Box>
          <Button
            id="save-button"
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disableElevation
          >
            {t("OC_SAVE")}
          </Button>
        </Box>
        <Box paddingTop="36px">
          <Button
            id="cancel-button"
            variant="text"
            color="secondary"
            fullWidth
            disableElevation
            onClick={handleLogout}
          >
            {t("OC_CANCEL")}
          </Button>
        </Box>
      </form>
    </>
  );
};

export default MyHrChangePasswordForm;
